import type { ExtractedLabItem } from './aiLabExtract';
import type { LabResult } from './localData';

export interface LabNameEnrichment {
  resultId: string;
  extractedIndex: number;
  originalName: string;
  russianName?: string;
  internationalName?: string;
  canonicalCode?: string;
  aliases?: string[];
  score: number;
}

export interface LabEnrichmentMatchResult {
  matches: LabNameEnrichment[];
  unmatchedResultIds: string[];
  unusedExtractedIndexes: number[];
}

function normalizeText(value?: string): string {
  return (value || '')
    .normalize('NFKC')
    .toLocaleLowerCase('ru-RU')
    .replace(/[(){}.,:;_\-/\\]+|\[|\]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function numericEqual(left?: number, right?: number): boolean {
  if (left === undefined || right === undefined) return false;
  const scale = Math.max(1, Math.abs(left), Math.abs(right));
  return Math.abs(left - right) <= scale * 1e-9;
}

function normalizedNames(result: LabResult): Set<string> {
  return new Set([
    result.analyte,
    result.originalName,
    result.russianName,
    result.internationalName,
    result.canonicalCode,
    ...(result.aliases || []),
  ].map(normalizeText).filter(Boolean));
}

function extractedNames(item: ExtractedLabItem): Set<string> {
  return new Set([
    item.analyte,
    item.russianName,
    item.internationalName,
    item.canonicalCode,
    ...(item.aliases || []),
  ].map(normalizeText).filter(Boolean));
}

function valuesMatch(result: LabResult, item: ExtractedLabItem): boolean {
  if (result.value !== undefined || item.value !== undefined) {
    return numericEqual(result.value, item.value);
  }
  return Boolean(result.valueText && item.valueText && normalizeText(result.valueText) === normalizeText(item.valueText));
}

function candidateScore(result: LabResult, item: ExtractedLabItem): number | null {
  if (!valuesMatch(result, item)) return null;

  const resultUnit = normalizeText(result.unit);
  const itemUnit = normalizeText(item.unit);
  if (resultUnit && itemUnit && resultUnit !== itemUnit) return null;

  if (result.refLow !== undefined && item.referenceLow !== undefined && !numericEqual(result.refLow, item.referenceLow)) return null;
  if (result.refHigh !== undefined && item.referenceHigh !== undefined && !numericEqual(result.refHigh, item.referenceHigh)) return null;

  let score = 100;
  if (resultUnit && itemUnit) score += 20;
  if (result.refLow !== undefined && item.referenceLow !== undefined) score += 8;
  if (result.refHigh !== undefined && item.referenceHigh !== undefined) score += 8;
  if (result.panel && item.panel && normalizeText(result.panel) === normalizeText(item.panel)) score += 8;

  const currentNames = normalizedNames(result);
  const sourceNames = extractedNames(item);
  let nameMatches = 0;
  currentNames.forEach(name => { if (sourceNames.has(name)) nameMatches += 1; });
  score += Math.min(60, nameMatches * 30);
  return score;
}

/**
 * One-to-one, conservative matching. A result is updated only when its best
 * candidate has a unique score. Equal-score candidates are intentionally left
 * unmatched so medical rows are never enriched by guessing.
 */
export function matchLabResultsForEnrichment(results: LabResult[], extracted: ExtractedLabItem[]): LabEnrichmentMatchResult {
  const proposals = results.flatMap(result => {
    const candidates = extracted
      .map((item, extractedIndex) => ({ extractedIndex, item, score: candidateScore(result, item) }))
      .filter((candidate): candidate is { extractedIndex: number; item: ExtractedLabItem; score: number } => candidate.score !== null)
      .sort((a, b) => b.score - a.score);
    if (!candidates.length) return [];
    if (candidates[1] && candidates[0].score === candidates[1].score) return [];
    return [{ result, ...candidates[0], margin: candidates[1] ? candidates[0].score - candidates[1].score : candidates[0].score }];
  }).sort((a, b) => b.score - a.score || b.margin - a.margin);

  const usedResults = new Set<string>();
  const usedExtracted = new Set<number>();
  const matches: LabNameEnrichment[] = [];

  for (const proposal of proposals) {
    if (usedResults.has(proposal.result.id) || usedExtracted.has(proposal.extractedIndex)) continue;
    usedResults.add(proposal.result.id);
    usedExtracted.add(proposal.extractedIndex);
    matches.push({
      resultId: proposal.result.id,
      extractedIndex: proposal.extractedIndex,
      originalName: proposal.item.analyte.trim(),
      russianName: proposal.item.russianName?.trim() || undefined,
      internationalName: proposal.item.internationalName?.trim() || undefined,
      canonicalCode: proposal.item.canonicalCode?.trim() || undefined,
      aliases: proposal.item.aliases?.map(alias => alias.trim()).filter(Boolean),
      score: proposal.score,
    });
  }

  return {
    matches,
    unmatchedResultIds: results.filter(result => !usedResults.has(result.id)).map(result => result.id),
    unusedExtractedIndexes: extracted.map((_, index) => index).filter(index => !usedExtracted.has(index)),
  };
}
